"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Interpreter = void 0;
const ast_1 = require("../parser/ast");
const token_1 = require("../lexer/token");
const functions_1 = require("../functions");
const basic_1 = require("../../formula_parser/functions/basic");
const model_1 = require("../../model");
const types_1 = require("../../types");
const utils_1 = require("../../utils");
const util_1 = require("util");
const i18n_1 = require("../../exports/i18n");
const lodash_1 = require("lodash");
class Interpreter {
    constructor(resolver, scope) {
        this.resolver = resolver;
        this.context = scope;
    }
    transformNodeValue(node, value, tokenType) {
        // Nodes of field value types need to perform dedicated string conversion logic
        if (node.name === ast_1.AstNodeType.ValueOperandNode || node.name === ast_1.AstNodeType.PureValueOperandNode) {
            const field = node.field;
            const fieldBasicValueType = model_1.Field.bindContext(field, this.context.state).basicValueType;
            /**
             * DateTime type is for comparison operators and needs to be compared in timestamp format,
             * @example {start time} = TODAY()
             */
            if ([types_1.BasicValueType.Number, types_1.BasicValueType.Boolean, types_1.BasicValueType.String].includes(fieldBasicValueType) ||
                (fieldBasicValueType === types_1.BasicValueType.DateTime &&
                    [
                        token_1.TokenType.Equal,
                        token_1.TokenType.NotEqual,
                        token_1.TokenType.Less,
                        token_1.TokenType.LessEqual,
                        token_1.TokenType.Greater,
                        token_1.TokenType.GreaterEqual,
                    ].includes(tokenType))) {
                return value;
            }
            if (fieldBasicValueType === types_1.BasicValueType.Array) {
                if (!(value === null || value === void 0 ? void 0 : value.length))
                    return null;
                switch (node.innerValueType) {
                    // directly take the first value for calculation
                    case types_1.BasicValueType.Number: {
                        if (value.length > 1) {
                            throw new basic_1.FormulaBaseError('');
                        }
                        return Number(value[0]);
                    }
                    default:
                        return model_1.Field.bindContext(field, this.context.state).arrayValueToString(value);
                }
            }
            return model_1.Field.bindContext(field, this.context.state).cellValueToString(value);
        }
        return value;
    }
    /**
     * Evaluate by accessing the abstract syntax tree
     * @param {AstNode} node
     * @returns {any}
     * @memberof Interpreter
     */
    visit(node, isErrorScope = false) {
        if (!node) {
            throw new TypeError('A AST Node is required to visit');
        }
        try {
            switch (node.name) {
                case ast_1.AstNodeType.BinaryOperatorNode: {
                    return this.visitBinaryOperatorNode(node);
                }
                case ast_1.AstNodeType.StringOperandNode: {
                    return this.visitStringOperatorNode(node);
                }
                case ast_1.AstNodeType.CallOperandNode: {
                    return this.visitCallOperatorNode(node, isErrorScope);
                }
                case ast_1.AstNodeType.NumberOperandNode: {
                    return this.visitNumberOperatorNode(node);
                }
                case ast_1.AstNodeType.UnaryOperatorNode: {
                    return this.visitUnaryOperatorNode(node);
                }
                case ast_1.AstNodeType.ValueOperandNode: {
                    return this.visitValueOperandNode(node);
                }
                case ast_1.AstNodeType.PureValueOperandNode: {
                    return this.visitPureValueOperandNode(node);
                }
                default: return;
            }
        }
        catch (e) {
            if (isErrorScope) {
                return e;
            }
            throw e;
        }
        throw new TypeError(`Unexpected AST Node Type: ${node.name}`);
    }
    visitBinaryOperatorNode(node) {
        var _a, _b;
        const tokenType = node.token.type;
        let left = this.transformNodeValue(node.left, this.visit(node.left), tokenType);
        let right = this.transformNodeValue(node.right, this.visit(node.right), tokenType);
        // Perform value conversion processing on the BLANK function
        if (((_a = node.left.token) === null || _a === void 0 ? void 0 : _a.value.toUpperCase()) === 'BLANK') {
            left = (0, basic_1.getBlankValueByType)(node.right.valueType, right);
        }
        if (((_b = node.right.token) === null || _b === void 0 ? void 0 : _b.value.toUpperCase()) === 'BLANK') {
            right = (0, basic_1.getBlankValueByType)(node.left.valueType, left);
        }
        switch (tokenType) {
            case token_1.TokenType.And: {
                return Boolean(left && right);
            }
            case token_1.TokenType.Or: {
                return Boolean(left || right);
            }
            case token_1.TokenType.Add: {
                left = left == null ? 0 : left;
                right = right == null ? 0 : right;
                if ((0, util_1.isNumber)(left) && (0, util_1.isNumber)(right)) {
                    return (0, utils_1.plus)(left, right);
                }
                return left + right;
            }
            case token_1.TokenType.Minus: {
                left = left == null ? 0 : left;
                right = right == null ? 0 : right;
                return (0, utils_1.minus)(left, right);
            }
            case token_1.TokenType.Times: {
                left = left == null ? 0 : left;
                right = right == null ? 0 : right;
                return (0, utils_1.times)(left, right);
            }
            case token_1.TokenType.Div: {
                left = left == null ? 0 : left;
                right = right == null ? 0 : right;
                return (0, utils_1.divide)(left, right);
            }
            case token_1.TokenType.Mod: {
                left = left == null ? 0 : left;
                right = right == null ? 0 : right;
                return left % right;
            }
            case token_1.TokenType.Equal: {
                // tslint:disable-next-line: triple-equals
                return left == right; // eslint-disable-line eqeqeq
            }
            case token_1.TokenType.NotEqual: {
                // tslint:disable-next-line: triple-equals
                return left != right; // eslint-disable-line eqeqeq
            }
            case token_1.TokenType.Greater: {
                return left > right;
            }
            case token_1.TokenType.GreaterEqual: {
                return left >= right;
            }
            case token_1.TokenType.Less: {
                return left < right;
            }
            case token_1.TokenType.LessEqual: {
                return left <= right;
            }
            case token_1.TokenType.Concat: {
                return String(left == null ? '' : left) + String(right == null ? '' : right);
            }
            default: return;
        }
        throw new TypeError(`Visitor can't process AST Node Type: ${node.token.type}`);
    }
    visitUnaryOperatorNode(node) {
        if (node.token.type === token_1.TokenType.Not) {
            return !this.visit(node.child);
        }
        if (node.token.type === token_1.TokenType.Add) {
            return +this.visit(node.child);
        }
        if (node.token.type === token_1.TokenType.Minus) {
            return -this.visit(node.child);
        }
        throw new TypeError(`Visitor can't process AST Node Type: ${node.token.type}`);
    }
    visitValueOperandNode(node, originValue) {
        return this.resolver(node.value.slice(1, -1), originValue);
    }
    visitPureValueOperandNode(node, originValue) {
        return this.resolver(node.value, originValue);
    }
    visitStringOperatorNode(node) {
        return node.value.slice(1, -1);
    }
    visitCallOperatorNode(node, isErrorScope = false) {
        var _a;
        const fnName = node.value.toUpperCase();
        const fnClass = functions_1.Functions.get(fnName);
        if (!fnClass) {
            throw new TypeError((0, i18n_1.t)(i18n_1.Strings.function_err_not_found_function_name_as, {
                fnName,
            }));
        }
        // The IS_ERROR function needs to monitor whether the internal equation will report an error, 
        // and IF/SWITCH needs to execute until Error before reporting an error.
        // So here is a special mark for IS_ERROR/IF/SWITCH functions
        // TODO: Do ISERROR compatibility here first, and delete the user data after brushing
        if (fnName === 'ISERROR' || fnName === 'IS_ERROR' || fnName === 'IF' || fnName === 'SWITCH') {
            isErrorScope = true;
        }
        const params = node.params.map(param => {
            let value = this.visit(param, isErrorScope);
            let valueType = param.valueType;
            // Nodes of field value types need to perform dedicated string conversion logic
            if (param.name === ast_1.AstNodeType.ValueOperandNode || param.name === ast_1.AstNodeType.PureValueOperandNode) {
                const field = param.field;
                // If the parameter type is Array && is not accepted by the function parameter type
                if (!fnClass.func.acceptValueType.has(valueType) && valueType === types_1.BasicValueType.Array) {
                    const innerValueType = param.innerValueType;
                    // When the built-in type of the array is Number and there is only one item, the corresponding operation can be performed.
                    // If there are multiple items, in order to avoid user misunderstanding, take the form of direct error reporting
                    if (innerValueType === types_1.BasicValueType.Number) {
                        if ((value === null || value === void 0 ? void 0 : value.length) > 1) {
                            throw new basic_1.FormulaBaseError('');
                        }
                        value = value && value[0];
                    }
                    else {
                        // The built-in type of the array is not Number, directly converted to a string
                        value = (value === null || value === void 0 ? void 0 : value.length) && value.filter((v) => !(0, util_1.isNull)(v)).join(', ');
                    }
                    innerValueType && (valueType = innerValueType);
                }
                /**
                 * value value return rules (granularity is specific formula class):
                 * 1. If acceptValueType does not contain valueType, it will be processed uniformly by the cellValueToString method;
                 * 2. If you need to return the original value, you need to add the current valueType to acceptValueType;
                 */
                if (!fnClass.func.acceptValueType.has(valueType)) {
                    if ((0, lodash_1.isString)(value)) {
                        value = value.split(', ');
                    }
                    value = model_1.Field.bindContext(field, this.context.state).cellValueToString(value);
                }
            }
            else {
                // Convert array type value to string type. (In theory, only field values will have array types, so this will not take effect)
                if (!fnClass.func.acceptValueType.has(valueType) && valueType === types_1.BasicValueType.Array) {
                    value = String(value);
                }
                // Convert date type value to string type.
                if (!fnClass.func.acceptValueType.has(valueType) && valueType === types_1.BasicValueType.DateTime) {
                    value = value == null ? value : new Date(value).toISOString();
                }
            }
            return { node: param, value };
        });
        fnClass.func.validateParams(node.params);
        // pre-check for DateTime class functions
        // LAST_MODIFIED_TIME is calculated based on record.recordMeta without the value of other cells
        if (fnClass.func.type === types_1.FormulaFuncType.DateTime && params.length && ((_a = params[0]) === null || _a === void 0 ? void 0 : _a.value) == null && fnName !== 'LAST_MODIFIED_TIME') {
            return null;
        }
        return fnClass.func.func(params, this.context);
    }
    visitNumberOperatorNode(node) {
        return Number(node.value);
    }
}
exports.Interpreter = Interpreter;
//# sourceMappingURL=interpreter.js.map